import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Input, Button } from 'day8-ui';
import { validatePhone } from '../../../utils/profileValidation';
import { requestEmailChange, confirmEmailChange } from '../../../actions/user';

const EditContactSection = ({
  userFormData, onPhoneChange, notifications = {}, fieldErrors = {}, onFieldBlur, onEmailUpdated
}) => {
  const dispatch = useDispatch();
  const [changingEmail, setChangingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [pending, setPending] = useState(false);
  const [emailMsg, setEmailMsg] = useState(null);
  const [emailErr, setEmailErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const currentEmail = useMemo(() => userFormData.email || '', [userFormData.email]);

  const startChange = () => {
    setChangingEmail(true);
    setNewEmail('');
    setOtpCode('');
    setPending(false);
    setEmailMsg(null);
    setEmailErr(null);
  };

  const cancelChange = () => {
    setChangingEmail(false);
    setPending(false);
    setNewEmail('');
    setOtpCode('');
    setEmailMsg(null);
    setEmailErr(null);
  };

  const submitRequest = e => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    if (!newEmail || !newEmail.trim()) {
      setEmailErr('New email is required.');
      return;
    }
    setBusy(true);
    setEmailErr(null);
    setEmailMsg(null);
    requestEmailChange(newEmail.trim())()
      .then(res => {
        if (res.status === 'success') {
          setPending(true);
          setOtpCode('');
          setEmailMsg(res.message || 'We sent a 6-digit code to your new email.');
        } else {
          setEmailErr(res.message || 'Could not start email change.');
        }
      })
      .catch(() => setEmailErr('Could not start email change.'))
      .finally(() => setBusy(false));
  };

  const submitConfirm = e => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    const code = (otpCode || '').trim();
    if (!/^\d{6}$/.test(code)) {
      setEmailErr('Enter the 6-digit code from your email.');
      return;
    }
    setBusy(true);
    setEmailErr(null);
    dispatch(confirmEmailChange(code))
      .then(res => {
        if (res.status === 'success') {
          setEmailMsg('Email updated.');
          setChangingEmail(false);
          setPending(false);
          if (onEmailUpdated && res.data) {
            onEmailUpdated(res.data);
          }
        } else {
          setEmailErr(res.message || 'Could not confirm email change.');
        }
      })
      .catch(() => setEmailErr('Could not confirm email change.'))
      .finally(() => setBusy(false));
  };

  return (
    <div className="grid grid-gap-md margin-top-md margin-bottom-xl">
      <div className="col-12">
        <h3 className="yw-app-h3 dui-my-2">Contact</h3>
      </div>
      <div className="col-12 col-6@sm" data-account-edit-field="email">
        <Input
          label="EMAIL*"
          type="email"
          name="email"
          placeholder="eg: you@example.com"
          value={currentEmail}
          disabled
          readOnly
          errorMessage={fieldErrors.email || (notifications.error && notifications.error.email?.notificationMessage)}
        />
        <small className="dui-text-gray-500">
          Email is your login credential. Use verified change to update it.
        </small>
        {!changingEmail && (
          <div className="dui-mt-2">
            <Button type="button" className="button button--secondary button--sm" onClick={startChange}>
              Change email
            </Button>
          </div>
        )}
        {/* No nested <form> — parent ProfileEditForm already wraps in <form method=GET by default>. */}
        {changingEmail && !pending && (
          <div className="dui-mt-3">
            <Input
              label="NEW EMAIL*"
              type="email"
              name="emailChangeNew"
              autoComplete="off"
              placeholder="new@example.com"
              value={newEmail}
              onChange={e => setNewEmail(e.target.value)}
            />
            <div className="dui-mt-2 grid grid-gap-sm">
              <Button
                type="button"
                className="button button--primary button--sm"
                disabled={busy}
                onClick={submitRequest}
              >
                Send confirmation
              </Button>
              <Button type="button" className="button button--secondary button--sm" onClick={cancelChange} disabled={busy}>
                Cancel
              </Button>
            </div>
          </div>
        )}
        {changingEmail && pending && (
          <div className="dui-mt-3">
            <p className="dui-text-sm">
              Enter the 6-digit code we sent to <strong>{newEmail}</strong>.
            </p>
            <Input
              label="CONFIRMATION CODE*"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              name="emailChangeCode"
              autoComplete="one-time-code"
              placeholder="123456"
              value={otpCode}
              onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            />
            <div className="dui-mt-2 grid grid-gap-sm">
              <Button
                type="button"
                className="button button--primary button--sm"
                disabled={busy}
                onClick={submitConfirm}
              >
                Verify code
              </Button>
              <Button type="button" className="button button--secondary button--sm" onClick={cancelChange} disabled={busy}>
                Cancel
              </Button>
            </div>
          </div>
        )}
        {emailMsg && <p className="dui-text-green-700 dui-mt-2">{emailMsg}</p>}
        {emailErr && <p className="dui-text-red-700 dui-mt-2">{emailErr}</p>}
      </div>
      <div className="col-12 col-6@sm" data-account-edit-field="phone">
        <Input
          label="MOBILE PHONE*"
          type="tel"
          name="phone"
          placeholder="eg: +44 7123 456789"
          value={userFormData.phone || ''}
          onChange={onPhoneChange}
          onBlur={() => onFieldBlur && onFieldBlur(
            'phone',
            validatePhone(userFormData.phone, userFormData.country || userFormData.nationality)
          )}
          errorMessage={
            fieldErrors.phone
            || (notifications.error && notifications.error.phone?.notificationMessage)
          }
        />
        <small className="dui-text-gray-500">Include your country code, e.g. +44 for UK</small>
      </div>
    </div>
  );
};

export default EditContactSection;

EditContactSection.propTypes = {
  userFormData: PropTypes.object.isRequired,
  onPhoneChange: PropTypes.func,
  notifications: PropTypes.object,
  fieldErrors: PropTypes.object,
  onFieldBlur: PropTypes.func,
  onEmailUpdated: PropTypes.func
};
